import { useState, useCallback } from 'react';
import { 
  collection, 
  addDoc, 
  onSnapshot, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  or,
  doc,
  deleteDoc,
  getDoc,
  getDocs,
  limit,
  setDoc,
  updateDoc,
  writeBatch
} from 'firebase/firestore';
import { db, auth } from '../firebase';

export const useSocialService = () => {
  const [friends, setFriends] = useState([]);
  const [messages, setMessages] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);

  const subscribeToMessages = useCallback((userId, friendId) => {
    if (!userId || !friendId) {
      setMessages([]);
      return () => {};
    }

    console.log('Subscribing to messages between', userId, 'and', friendId);
    const participants = [userId, friendId].sort().join('_');
    
    // Mark messages as received when they are loaded
    const markMessagesAsReceived = async (messages) => {
      const batch = writeBatch(db);
      let hasUpdates = false;

      messages.forEach(msg => {
        if (msg.receiverId === userId && !msg.receivedAt && msg.status === 'sent') {
          const msgRef = doc(db, 'messages', msg.id);
          batch.update(msgRef, {
            status: 'received',
            receivedAt: serverTimestamp()
          });
          hasUpdates = true;
        }
      });

      if (hasUpdates) {
        try {
          await batch.commit();
        } catch (error) {
          console.error('Error marking messages as received:', error);
        }
      }
    };

    // Mark messages as read when they are viewed
    const markMessagesAsRead = async (messages) => {
      const batch = writeBatch(db);
      let hasUpdates = false;

      messages.forEach(msg => {
        if (msg.receiverId === userId && !msg.readAt && (msg.status === 'received' || msg.status === 'sent')) {
          const msgRef = doc(db, 'messages', msg.id);
          batch.update(msgRef, {
            status: 'read',
            readAt: serverTimestamp()
          });
          hasUpdates = true;
        }
      });

      if (hasUpdates) {
        try {
          await batch.commit();
        } catch (error) {
          console.error('Error marking messages as read:', error);
        }
      }
    };

    const messagesQuery = query(
      collection(db, 'messages'),
      where('participants', '==', participants),
      orderBy('timestamp', 'asc')
    );

    return onSnapshot(messagesQuery, (snapshot) => {
      console.log('Received messages update:', snapshot.docs.length, 'messages');
      const newMessages = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          text: data.text,
          senderId: data.senderId,
          receiverId: data.receiverId,
          timestamp: data.timestamp?.toDate() || new Date(),
          sender: data.senderId === userId ? 'me' : 'friend',
          status: data.status || 'sent',
          readAt: data.readAt?.toDate() || null,
          receivedAt: data.receivedAt?.toDate() || null,
          type: data.type,
          content: data.content,
          taskData: data.taskData
        };
      });

      // Mark messages as received when they arrive
      markMessagesAsReceived(newMessages);

      // Mark messages as read when they are viewed
      markMessagesAsRead(newMessages);

      console.log('Processed messages:', newMessages);
      setMessages(newMessages);
    }, error => {
      console.error('Error subscribing to messages:', error);
      setMessages([]);
    });
  }, []);

  // Update user's online status
  const updateOnlineStatus = useCallback(async (userId, isOnline) => {
    if (!userId) return;
    
    try {
      const userStatusRef = doc(db, 'status', userId);
      const userStatusData = {
        state: isOnline ? 'online' : 'offline',
        lastChanged: serverTimestamp(),
        userId // Add userId to the document for security rules
      };
      
      await setDoc(userStatusRef, userStatusData, { merge: true });
    } catch (error) {
      console.error('Error updating online status:', error);
    }
  }, []);

  // Subscribe to friends' online status
  const subscribeToFriends = useCallback((userId, userEmail) => {
    if (!userId) return () => {};

    // Update current user's online status
    updateOnlineStatus(userId, true);

    // Set up offline status
    const handleOffline = () => {
      updateOnlineStatus(userId, false);
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleOffline);
    window.addEventListener('pagehide', handleOffline);

    // Query for friends
    const friendsRef = collection(db, 'friends');
    const friendsQuery = query(
      friendsRef,
      or(
        where('userId', '==', userId),
        where('friendId', '==', userId)
      )
    );

    // Subscribe to friends list and their status
    const unsubscribe = onSnapshot(friendsQuery, (snapshot) => {
      if (snapshot.empty) {
        setFriends([]);
        return;
      }

      Promise.all(
        snapshot.docs.map(async (docSnapshot) => {
          try {
            const data = docSnapshot.data();
            // Fix email mapping
            const isSender = data.userId === userId;
            const friendId = isSender ? data.friendId : data.userId;
            const friendEmail = isSender ? data.friendEmail : data.senderEmail;

            // Get friend's online status
            const statusRef = doc(db, 'status', friendId);
            const statusDoc = await getDoc(statusRef);

            return {
              id: docSnapshot.id,
              userId: friendId,
              email: friendEmail,
              status: statusDoc.exists() ? statusDoc.data().state : 'offline',
              lastChanged: statusDoc.exists() ? statusDoc.data().lastChanged : null
            };
          } catch (error) {
            console.error('Error processing friend data:', error);
            return null;
          }
        })
      )
      .then((results) => {
        // Filter out any null results from errors
        const validFriends = results.filter(friend => friend !== null);
        setFriends(validFriends);
      })
      .catch((error) => {
        console.error('Error getting friends status:', error);
        setFriends([]);
      });
    }, (error) => {
      console.error('Error in friends subscription:', error);
      setFriends([]);
    });

    // Return cleanup function
    return () => {
      window.removeEventListener('beforeunload', handleOffline);
      window.removeEventListener('pagehide', handleOffline);
      updateOnlineStatus(userId, false);
      unsubscribe();
    };
  }, [updateOnlineStatus]);

  const sendMessage = async (senderId, receiverId, text) => {
    if (!text.trim() || !senderId || !receiverId) {
      throw new Error('Invalid message data');
    }

    const participants = [senderId, receiverId].sort().join('_');
    
    const messageData = {
      text: text.trim(),
      senderId,
      receiverId,
      participants,
      timestamp: serverTimestamp(),
      createdAt: new Date().toISOString(),
      status: 'sent',
      readAt: null,
      receivedAt: null
    };

    try {
      const messageRef = await addDoc(collection(db, 'messages'), messageData);
      return messageRef.id;
    } catch (error) {
      console.error('Error sending message:', error);
      throw new Error('Failed to send message. Please try again.');
    }
  };

  const sendFriendRequest = async (userId, recipientEmail) => {
    // Check if a request already exists
    const existingRequestsQuery = query(
      collection(db, 'friendRequests'),
      where('userId', '==', userId),
      where('recipientEmail', '==', recipientEmail)
    );

    const existingRequests = await getDocs(existingRequestsQuery);
    if (!existingRequests.empty) {
      throw new Error('Friend request already sent');
    }

    // Check if they're already friends
    const existingFriendsQuery = query(
      collection(db, 'friends'),
      where('userId', '==', userId),
      where('friendEmail', '==', recipientEmail)
    );

    const existingFriends = await getDocs(existingFriendsQuery);
    if (!existingFriends.empty) {
      throw new Error('Already friends with this user');
    }

    // Use the current user's email from auth
    const senderEmail = auth.currentUser?.email;
    if (!senderEmail) {
      throw new Error('You must be logged in with an email to send friend requests');
    }

    // Don't allow sending request to yourself
    if (senderEmail === recipientEmail) {
      throw new Error('Cannot send friend request to yourself');
    }

    const friendRequest = {
      userId,
      senderEmail,
      recipientEmail,
      status: 'pending',
      timestamp: serverTimestamp()
    };

    return addDoc(collection(db, 'friendRequests'), friendRequest);
  };

  const acceptFriendRequest = async (requestId, userId) => {
    try {
      // Get the request details
      const requestDoc = await getDoc(doc(db, 'friendRequests', requestId));
      if (!requestDoc.exists()) {
        throw new Error('Friend request not found');
      }

      const requestData = requestDoc.data();
      const currentUserEmail = auth.currentUser?.email;

      if (!currentUserEmail) {
        throw new Error('User email not found');
      }

      // Create a single friend document that both users can access
      const friendDoc = {
        userId: requestData.userId, // sender's ID
        friendId: userId, // recipient's ID
        senderEmail: requestData.senderEmail, // sender's email
        friendEmail: currentUserEmail, // recipient's email
        status: 'accepted',
        timestamp: serverTimestamp()
      };

      // Add the friend document
      await addDoc(collection(db, 'friends'), friendDoc);

      // Delete the friend request
      await deleteDoc(doc(db, 'friendRequests', requestId));

      return true;
    } catch (error) {
      console.error('Error accepting friend request:', error);
      throw error;
    }
  };

  const rejectFriendRequest = async (requestId) => {
    try {
      await deleteDoc(doc(db, 'friendRequests', requestId));
      return true;
    } catch (error) {
      console.error('Error rejecting friend request:', error);
      throw error;
    }
  };

  const shareTask = async (senderId, receiverId, task) => {
    if (!senderId || !receiverId || !task) {
      throw new Error('Invalid share data');
    }

    const participants = [senderId, receiverId].sort().join('_');
    
    const messageData = {
      text: `📋 Task: "${task.title}"\n━━━━━━━━━━━━━━━\n🕒 Due: ${task.dueDate ? new Date(task.dueDate).toLocaleDateString() : 'No due date'}\n📊 Status: ${task.status || 'Not started'}\n\n${task.description || 'No description'}\n━━━━━━━━━━━━━━━`,
      senderId,
      receiverId,
      participants,
      timestamp: serverTimestamp(),
      createdAt: new Date().toISOString(),
      type: 'task',
      taskData: task
    };

    try {
      const messageRef = await addDoc(collection(db, 'messages'), messageData);
      return messageRef.id;
    } catch (error) {
      console.error('Error sharing task:', error);
      throw new Error('Failed to share task. Please try again.');
    }
  };

  const shareBook = async (senderId, receiverId, book) => {
    if (!senderId || !receiverId || !book) {
      throw new Error('Invalid share data');
    }

    const participants = [senderId, receiverId].sort().join('_');
    
    const messageData = {
      senderId,
      receiverId,
      participants,
      timestamp: serverTimestamp(),
      createdAt: new Date().toISOString(),
      type: 'book',
      content: {
        bookId: book.id,
        title: book.title,
        author: book.author,
        status: book.status,
        rating: book.rating,
        progress: book.progress,
        pages: book.pages,
        coverUrl: book.coverUrl
      }
    };

    try {
      const messageRef = await addDoc(collection(db, 'messages'), messageData);
      return messageRef.id;
    } catch (error) {
      console.error('Error sharing book:', error);
      throw new Error('Failed to share book. Please try again.');
    }
  };

  const shareHabit = async (senderId, receiverId, habit) => {
    if (!senderId || !receiverId || !habit) {
      throw new Error('Invalid share data');
    }

    const participants = [senderId, receiverId].sort().join('_');
    
    const messageData = {
      senderId,
      receiverId,
      participants,
      timestamp: serverTimestamp(),
      createdAt: new Date().toISOString(),
      type: 'habit',
      content: {
        habitId: habit.id,
        name: habit.name,
        streak: habit.streak,
        completedDates: habit.completedDates || []
      }
    };

    try {
      const messageRef = await addDoc(collection(db, 'messages'), messageData);
      return messageRef.id;
    } catch (error) {
      console.error('Error sharing habit:', error);
      throw new Error('Failed to share habit. Please try again.');
    }
  };

  const shareMindfulness = async (senderId, receiverId, session) => {
    if (!senderId || !receiverId || !session) {
      throw new Error('Invalid share data');
    }

    const participants = [senderId, receiverId].sort().join('_');
    
    const messageData = {
      senderId,
      receiverId,
      participants,
      timestamp: serverTimestamp(),
      createdAt: new Date().toISOString(),
      type: 'mindfulness',
      content: {
        sessionId: session.id,
        type: session.type,
        duration: session.duration,
        completedAt: session.completedAt,
        mood: session.mood || null
      }
    };

    try {
      const messageRef = await addDoc(collection(db, 'messages'), messageData);
      return messageRef.id;
    } catch (error) {
      console.error('Error sharing mindfulness session:', error);
      throw new Error('Failed to share mindfulness session. Please try again.');
    }
  };

  const getUserTasks = async (userId) => {
    try {
      const tasksQuery = query(
        collection(db, 'tasks'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(tasksQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching tasks:', error);
      throw new Error('Failed to fetch tasks. Please try again.');
    }
  };

  return {
    friends,
    messages,
    pendingRequests,
    subscribeToMessages,
    subscribeToFriends,
    sendMessage,
    sendFriendRequest,
    acceptFriendRequest,
    rejectFriendRequest,
    shareTask,
    shareBook,
    shareHabit,
    shareMindfulness,
    getUserTasks,
    updateOnlineStatus
  };
}; 